import { atom } from "jotai";
import type { PrimitiveAtom, Setter } from "jotai";
import { currentUserAtom, userFetchErrorAtom } from "./userAtoms";
import {
    locationsAtom,
    locationsFetchErrorAtom,
    locationsLoadedAtom,
} from "./locationAtoms";
import { shoppingCartAtom } from "./shoppingCartAtoms";
import {
    adminOrderDetailAtom,
    feeChargeBlocksAtom,
    meOrdersFetchErrorAtom,
    orderStatusCountsAtom,
    ordersListAtom,
} from "./orderAtoms";
import {
    locationStatusCountsAtom,
    organizationDetailAtom,
    organizationsAtom,
    organizationsListAtom,
} from "./organizationAtoms";
import { dashboardAtom } from "./dashboardAtoms";
import { unconfirmedChargesAtom } from "./billingAtoms";
import { productsAtom } from "./productAtoms";
import {
    productFacetsAtom,
    productFacetsStatusAtom,
} from "./productFacetAtoms";
import {
    productSuggestionsAtom,
    productSuggestionsStatusAtom,
} from "./productSuggestionAtoms";
import { categoriesAtom, categoriesFetchErrorAtom } from "./categoryAtoms";
import { catalogsAtom } from "./catalogAtoms";
import { optionsAtom } from "./optionAtoms";
import { optionValuesAtom } from "./optionValueAtoms";
import { vendorsAtom } from "./vendorAtoms";
import { legalClassRestrictionsAtom } from "./legalClassRestrictionAtoms";

// Bumped every time a session ends. A setter from useSessionSetAtom belongs to
// the session it was rendered in and drops a write made after the bump, so a
// response that lands after sign-out cannot repopulate what endSessionAtom
// just cleared.
export const sessionEpochAtom = atom(0);

// Back to the value the atom was declared with — its own default, so the reset
// cannot drift from a default edited later.
const clear = <Value>(
    set: Setter,
    anAtom: PrimitiveAtom<Value> & { init: Value },
) => set(anAtom, anAtom.init);

// Clears everything read from the signed-in account's API (Sklar ruling,
// PR 287; PO ruling 2026-09-28): the payloads and the read-failed flags, since
// a failure belongs to the session that saw it. Deliberately NOT cleared:
//   - in-flight flags (isFetching…, isProcessing…): left to settle in their
//     own `finally`.
//   - UI state (open panels, sheets, drawers, toasts).
//   - the shipping policy: an anonymous read, the same for every visitor.
//   - persisted preferences (stored ship-to id, recent searches): this reset
//     also fires on every page load (see useEndSessionOnSignOut), so they are
//     cleared by an explicit sign-out instead (useSignOut).
export const endSessionAtom = atom(null, (_get, set) => {
    set(sessionEpochAtom, (epoch) => epoch + 1);

    // The account itself; the ship-to atoms derive from these.
    clear(set, currentUserAtom);
    clear(set, userFetchErrorAtom);
    clear(set, locationsAtom);
    clear(set, locationsLoadedAtom);
    clear(set, locationsFetchErrorAtom);
    clear(set, organizationsAtom);

    // Shopping.
    clear(set, shoppingCartAtom);
    clear(set, meOrdersFetchErrorAtom);

    // Catalogue reads, answered for this account's ship-to location.
    clear(set, productsAtom);
    clear(set, productFacetsAtom);
    clear(set, productFacetsStatusAtom);
    clear(set, productSuggestionsAtom);
    clear(set, productSuggestionsStatusAtom);
    clear(set, categoriesAtom);
    clear(set, categoriesFetchErrorAtom);

    // Admin.
    clear(set, ordersListAtom);
    clear(set, orderStatusCountsAtom);
    clear(set, adminOrderDetailAtom);
    clear(set, feeChargeBlocksAtom);
    clear(set, organizationsListAtom);
    clear(set, organizationDetailAtom);
    clear(set, locationStatusCountsAtom);
    clear(set, dashboardAtom);
    clear(set, unconfirmedChargesAtom);
    clear(set, catalogsAtom);
    clear(set, optionsAtom);
    clear(set, optionValuesAtom);
    clear(set, vendorsAtom);
    clear(set, legalClassRestrictionsAtom);
});
